// FieldWorks rebuild design system — formatting helpers.
//
// Pure functions; no React. The clock formats are what BigClock, LiveCard and
// PersonRow render, and what a worker reads as "how long have I been here" —
// so they are pinned by fwFormat.test.js rather than trusted.

/**
 * Two-letter initials for an avatar: first letter of the first and last
 * words. "Marcus Reyes" → "MR", "Cher" → "C", "" → "".
 */
export function initialsOf(name) {
  if (!name || typeof name !== 'string') return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/** Clamp to a whole, non-negative number of seconds. Never a negative clock. */
function wholeSeconds(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/**
 * Elapsed seconds → 'H:MM'. Hours are unpadded (0:05, 12:30), minutes are
 * always two digits. Render with .fw-num so a column of these lines up.
 */
export function formatClock(seconds) {
  const s = wholeSeconds(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Elapsed seconds → { hm: 'H:MM', s: 'SS' }. BigClock renders `hm` large and
 * `s` in a small span, which is why they come back separately.
 */
export function formatClockWithSeconds(seconds) {
  const s = wholeSeconds(seconds);
  return { hm: formatClock(s), s: String(s % 60).padStart(2, '0') };
}

/**
 * Decimal hours → '8.25'. Always two decimals so a column of hours lines up
 * under .fw-num; this is display only and never rounds for pay — payroll
 * figures come from the server as they are.
 */
export function formatHours(h) {
  const n = Number(h);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

/**
 * Seconds between `startedAt` (ISO string, Date, or epoch ms) and `now`
 * (Date or epoch ms). Clamped at 0 — a phone whose clock is behind the
 * server's must never show a negative shift.
 */
export function elapsedSeconds(startedAt, now = Date.now()) {
  const start = startedAt instanceof Date ? startedAt.getTime() : new Date(startedAt).getTime();
  const end = now instanceof Date ? now.getTime() : Number(now);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return wholeSeconds((end - start) / 1000);
}
